<template>
  <draggable
    ref="drag"
    v-model="files"
    v-bind="dragOptions"
    multi-drag
    tag="tbody"
    selected-class="multi-drag"
    @update="onUpdate"
    @add="onAdd"
    @remove="onRemove"
    @start="onDragStart"
    @end="onDragEnd"
  >
    <template v-if="files.length > 0">
      <tr v-for="item in files" :key="item.id">
        <td v-if="!details" class="d-flex align-center">
          <div class="handle-drag" style="cursor: move">
            <v-icon>mdi-drag</v-icon>
          </div>
          <v-checkbox
            hide-details
            class="pa-0 ma-0"
            v-model="selectedNfe[item.id]"
            @change="onCheckboxChange"
          ></v-checkbox>
        </td>
        <td>
          <span>
            <v-tooltip color="transparent" right nudge-left="25" nudge-bottom="30" max-width="330">
              <template v-slot:activator="{ on, attrs }">
                <span>{{ item.number }}</span>
                <v-icon small @click="copy(item.accessKey)" v-bind="attrs" v-on="on">
                  mdi-checkbox-multiple-blank-outline
                </v-icon>
              </template>
              <span class="open-box">
                <v-card>
                  <v-card-text class="p-1" style="font-size: 10px; padding-top: 5px; padding-bottom: 5px">
                    Clique para copiar a chave de acesso:
                    {{ item.accessKey }}
                  </v-card-text>
                </v-card>
              </span>
            </v-tooltip>
          </span>
        </td>
        <td>{{ formatPrice(item.price) }}</td>
        <td>{{ formatWeight(item.weight) }}kg</td>
      </tr>
    </template>
    <template v-else>
      <tr>
        <td colspan="5" class="text-center">Nenhuma NFe alocado para essa entrega</td>
      </tr>
    </template>
  </draggable>
</template>

<script>
import copyToClipboard from '@/utils/copyToClipboard';
import draggable from 'vuedraggable';
import maskPrice from '@/utils/maskPrice';
import maskWeight from '@/utils/maskWeight';
import { mapActions } from 'vuex';

export default {
  components: {
    draggable
  },
  props: {
    value: Array,
    selectAll: {
      type: Boolean,
      default: false
    },
    delivery: {
      type: Object,
      default: () => {}
    },
    details:{
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedNfe: {},
      dragOptions: {
        animation: 150,
        group: 'description',
        ghostClass: 'ghost',
        disabled: false,
        handle: '.handle-drag'
      },
      isDragging: false
    };
  },
  computed: {
    files: {
      get() {
        return this.value;
      },
      set(newValue) {
        this.$emit('input', newValue);
      }
    }
  },
  watch: {
    selectAll() {
      this.toggleSelectAll();
    },
    files: {
      handler() {
        if (!this.files.length) {
          this.$emit('update:selectAll', false);
        }
      }
    },
    selectedNfe: {
      handler() {
        this.checkAllSelected();
      },
      deep: true
    }
  },
  methods: {
    ...mapActions('ImportManualDeliveryStore', ['allocateNfes', 'deallocateNfes']),
    async allocateNfesToDelivery(nfes) {
      try {
        let nfeIds;
        if (Array.isArray(nfes)) {
          nfeIds = nfes.map(({ id }) => id);
        } else {
          nfeIds = [nfes.id];
        }
        const params = {
          id: this.delivery.id,
          nfeIds: nfeIds
        };

        const response = await this.allocateNfes(params);
        if (response.success) {
          this.$emit('update:allocatedNfes', 'NFes alocadas com sucesso!', true);
          this.$emit('update:selectAll', false);
        } else {
          const draggedItemIndex = this.files.findIndex((item) => item === nfes);
          this.files.splice(draggedItemIndex, 1);
          this.$emit(
            'update:allocatedNfes',
            response.errors[0] || 'NFes com destinatário ou remetente distintos!',
            false
          );
        }
      } catch (err) {
        console.error(err);
      }
    },

    async deallocateNfesFromDelivery(nfes) {
      try {
        let nfeIds;
        if (Array.isArray(nfes)) {
          nfeIds = nfes.map(({ id }) => id);
        } else {
          nfeIds = [nfes.id];
        }
        const params = {
          id: this.delivery.id,
          nfeIds: nfeIds
        };
        const response = await this.deallocateNfes(params);
        if (response.success) {
          this.$emit('update:allocatedNfes', 'NFes desalocadas com sucesso!', true);
        } else {
          this.$emit('update:allocatedNfes', response.errors[0] || 'Erro ao desalocar NFes', false);
        }
      } catch (err) {
        console.error(err);
      }
    },
    toggleSelectAll() {
      this.selectedNfe = {};
      if (this.selectAll) {
        this.files.forEach((nfe) => {
          this.$set(this.selectedNfe, nfe.id, true);
        });
      } else {
        this.selectedNfe = {};
      }
    },
    updateNfeSelection(id) {
      this.$set(this.selectedNfe, id, !this.selectedNfe[id]);
    },
    onDragStart(e) {
      if (this.files.length > 1) {
        e.item._selected_items = this.files.filter((f) => this.selectedNfe[f.id]);
      } else {
        e.item._selected_items = this.files[0];
      }
    },
    onDragEnd() {
      this.isDragging = false;
      this.selectedNfe = {};
    },
    onUpdate(e) {
      if (e.item._selected_items.length) {
        const ids = e.item._selected_items.map(({ id }) => id);
        const files = this.files.filter((f) => !ids.includes(f.id));
        files.splice(e.newIndex, 0, ...e.item._selected_items);
        this.files = files;
      }
    },
    onRemove(e) {
      let item;
      if (e.item._selected_items.length > 1) {
        item = e.item._selected_items;
      } else {
        item = e.item._underlying_vm_;
      }
      this.deallocateNfesFromDelivery(item);
      this.$emit('update:selectAll', false);
    },
    async onAdd(e) {
      this.$emit('loading:allocatedNfes', true);
      if (e.item._selected_items.length > 1) {
        const draggedItems = e.item._selected_items;
        await this.allocateNfesToDelivery(draggedItems);
      } else {
        await this.allocateNfesToDelivery(e.item._underlying_vm_);
      }
      this.$emit('loading:allocatedNfes', false);
      this.$emit('update:selectAll', false);
    },
    toggleSelect(id) {
      this.$set(this.selectedNfe, id, !this.selectedNfe[id]);
      this.checkAllSelected();
    },
    onCheckboxChange() {
      this.checkAllSelected();
    },
    checkAllSelected() {
      const allSelected = this.files.length > 0 && this.files.every((nfe) => this.selectedNfe[nfe.id]);
      this.$emit('update:selectAll', allSelected);
    },
    formatPrice(price) {
      return maskPrice(price);
    },
    formatWeight(price) {
      return maskWeight(price);
    },
    copy(str) {
      copyToClipboard(str);
    }
  }
};
</script>

<style scoped>
.ghost {
  display: none;
}
</style>
